import React from "react";
import { Flex } from "grid-styled";
import { ellipsis } from "polished";
import styled, { css } from "styled-components";
import RouterLink from "redux-first-router-link";
import { H2, Icon, IconButton, IconButtonInnerChild, Popup as PopupUI, transition } from "@evergis/ui";
import { IIconButtonProps } from "@evergis/ui/dist/molecules/IconButton/types";

import { PROJECT_PANEL_HEADER_HEIGHT } from "./constants";

const DarkMainContainerMixin = css`
  :before {
    background: rgba(0, 0, 0, 0.28);
  }
`;

export const MainContainer = styled(Flex)<{ $isDark?: boolean }>`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  flex-direction: column;
  padding: 1.625rem 1.5rem 1.5rem;
  max-height: ${PROJECT_PANEL_HEADER_HEIGHT};
  background-color: ${({ theme: { palette } }) => palette.primary};
  border-radius: ${({ theme: { borderRadius } }) => `0 0 ${borderRadius.medium} ${borderRadius.medium}`};
  overflow: hidden;

  :before,
  :after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  :after {
    background: radial-gradient(
      129.21% 133.22% at 51.94% 0%,
      ${({ $isDark }) => ($isDark ? "rgba(0, 0, 0, 0.88)" : "rgba(255, 255, 255, 0.88)")} 27.17%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  ${({ $isDark }) => $isDark && DarkMainContainerMixin}
  > * {
    z-index: 1;
  }
`;

export const ProjectContent = styled(Flex)`
  flex-direction: column;
  width: 100%;
  min-width: 1px;
  flex-grow: 1;
`;

export const PreloaderText = styled.div`
  margin-top: 0.375rem;
  font: ${({ theme: { fonts } }) => fonts.description};
  color: ${({ theme: { palette } }) => palette.textSecondary};
  line-height: normal;
`;

export const TitleContainer = styled(Flex)`
  justify-content: space-between;
  flex-wrap: nowrap;
  width: 100%;
  align-items: flex-start;
`;

export const ProjectTitle = styled(H2)`
  ${ellipsis() as string};
  margin: 0;
  font-weight: normal;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

type VerticalMenuContainerProps = {
  disabled?: boolean;
};

export const VerticalMenuContainer = styled(Flex)<VerticalMenuContainerProps>`
  margin: -0.125rem -0.6rem 0 0;

  &[disabled] {
    pointer-events: none;
    opacity: 0.28;
  }

  button span[kind="menu_vertical"],
  button:hover span[kind="menu_vertical"] {
    color: ${({ theme: { palette } }) => palette.icon};
  }
`;

export const Popup = styled(PopupUI)`
  min-width: 18.75rem;
  max-width: 18.75rem;
  padding: 0.5rem 0;
`;

export const UserMenuContainer = styled(Flex)<{ $isUserMenuOpen?: boolean }>`
  flex-direction: column;
  margin: 0 0.5rem 0.5rem 0.5rem;
  padding: 1rem 0 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};

  .user-menu-options {
    margin-top: 0.5rem;
  }
`;

export const UserMenuHeader = styled(Flex)`
  padding: 0 0.5rem 0 1rem;
`;

export const UserMenuToolbar = styled(Flex)`
  width: 100%;
`;

export const UserMenuLeft = styled(Flex)`
  align-items: center;
  margin-right: 1rem;
`;

export const UserMenuRight = styled(Flex)`
  align-items: center;
  justify-content: center;
`;

export const SharedLocalizationTheme = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px ${({ theme: { palette } }) => palette.elementDeep} solid;
`;

export const UserMenuShowButton = styled(Flex)<{ $isOpen?: boolean }>`
  cursor: pointer;
  align-items: center;
  margin: 0.625rem auto ${({ $isOpen }) => ($isOpen ? "0.5rem" : 0)};
  font-size: 0.625rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
  transition: color ${transition.hover};

  span[kind="expand"] {
    margin: 0.125rem 0 0 0.25rem;
    transform: ${({ $isOpen }) => `rotate(${$isOpen ? 180 : 0}deg)`};
    transition: color ${transition.hover}, transform ${transition.hover} cubic-bezier(0.15, 1.35, 0.7, 1.5);
    transition: transform 300ms cubic-bezier(0.15, 1.35, 0.7, 1.5), opacity ${transition.hover};
  }

  :hover {
    color: ${({ theme: { palette } }) => palette.textPrimary};

    span[kind="expand"] {
      color: ${({ theme: { palette } }) => palette.textPrimary};
    }
  }
`;

const MainMenuDefaultMixin = css`
  min-width: 3rem;
  max-width: 3rem;

  ${IconButtonInnerChild} {
    color: transparent;
  }

  ${Icon} {
    color: ${({ theme: { palette } }) => palette.icon};
  }

  :hover {
    ${IconButtonInnerChild} {
      color: transparent;
    }

    ${Icon} {
      color: ${({ theme: { palette } }) => palette.icon};
    }
  }
`;

const MainMenuActiveMixin = css`
  max-width: 10rem;
  background-color: ${({ theme: { palette } }) => palette.iconAccent};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xxLarge};
  pointer-events: none;

  ${IconButtonInnerChild}, ${Icon} {
    color: ${({ theme: { palette } }) => palette.background};
  }
`;

export const MainMenuButton = styled(IconButton).attrs({ hasBg: true })<IIconButtonProps>`
  justify-content: flex-start;
  padding: 1.125rem 1rem;
  overflow: hidden;

  ${Icon} {
    min-width: 1rem;
  }

  ${IconButtonInnerChild}, ${Icon} {
    transition: none;
  }
`;

export const MainMenuContainer = styled(Flex)`
  margin-top: 1rem;

  ${MainMenuButton} {
    ${MainMenuDefaultMixin};

    &.disabled {
      cursor: default;
      opacity: 0.28;
    }
  }

  a {
    margin-right: 0.5rem;

    &.active {
      ${MainMenuButton} {
        ${MainMenuActiveMixin};
      }
    }
  }
`;

export const Link = styled(({ isActive, ...rest }) => <RouterLink {...rest} />)<{ isActive?: boolean }>`
  cursor: default;
  text-decoration: none;
  pointer-events: ${({ isActive }) => (isActive ? "none" : "default")};
`;

export const MenuTitle = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  width: 100%;

  a {
    margin-right: 0.5rem;

    img {
      border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
    }
  }
`;

export const Content = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;

export const FullName = styled.div`
  ${ellipsis() as string};
  max-width: 7.75rem;
  font-weight: 500;
`;

export const UserName = styled.span`
  ${ellipsis() as string};
  font: ${({ theme: { fonts } }) => fonts.description};
  color: ${({ theme: { palette } }) => palette.textSecondary};
`;

export const InfoButton = styled(IconButton).attrs({ kind: "info" })`
  opacity: 0.36;
  transition: opacity ${transition.hover};

  :hover {
    opacity: 1;
  }
`;
